import { useEffect, useMemo, useState } from "react";
import {
  loadStripe,
  StripeCheckoutPaymentElementOptions,
} from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
  ExpressCheckoutElement,
} from "@stripe/react-stripe-js";

import * as Api from "@modules/api";
import {
  createBehaviourEvent,
  createMyPaymentIntent,
  setAlert,
  useAppSelector,
  useCreateMyPaymentIntent,
} from "@lib/stores";
import { AlertType } from "@lib/stores/common/types";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import TranslatedText from "@components/items/display/TranslatedText";
import { translate } from "@modules/localizations/translate";

interface CheckoutProps {
  product: Api.Server.ProductGroupProductOutput;
  onCancel: () => void;
  onSuccess: () => void | Promise<void>;
  promoCode?: string;
}

let paymentIntentFetched = false;

export const Checkout = ({
  product,
  promoCode,
  onCancel,
  onSuccess,
}: CheckoutProps) => {
  return (
    <div className="flex h-full flex-col items-center justify-center">
      {product ? (
        <div className="mt-[13px]">
          <div className="mx-auto w-full">
            <StripeForm
              onCancel={onCancel}
              onSuccess={onSuccess}
              product={product}
              promoCode={promoCode}
              loadingStyle="border-black"
              price={
                <div className="flex items-center justify-center">
                  <div>{product.currencyCode || ""}</div>
                  <div className="ms-1">
                    {product.initialAmount ?? product.amount}
                  </div>
                  <div>{product.priceSuffix || ""}</div>
                </div>
              }
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

interface StripeFormProps {
  product: Api.Server.ProductGroupProductOutput;
  onCancel: () => void;
  onSuccess: () => void | Promise<void>;
  promoCode?: string;
  loadingStyle?: string;
  price?: string | JSX.Element;
}

export const StripeForm = ({
  product,
  onCancel,
  onSuccess,
  promoCode,
  loadingStyle = "border-white",
  price,
}: StripeFormProps) => {
  const token = useAppSelector(({ auth }) => auth.tokens.token);
  const userId = useAppSelector(({ auth }) => auth.user.id);
  const email = useAppSelector(({ auth }) => auth.user.email);

  const {
    data: createMyPaymentIntentData,
    status: createMyPaymentIntentStatus,
  } = useCreateMyPaymentIntent();

  const mapProductName = () => {
    switch (product.subscriptionPeriod) {
      case "P1W":
        return translate("subscription.periods.P1W");
      case "P1M":
        return translate("subscription.periods.P1M");
      case "P1Y":
        return translate("subscription.periods.P1Y");
      case "":
        return translate("subscription.periods.LIFE_TIME");
      default:
        return "";
    }
  };

  useEffect(() => {
    if (!token) {
      onCancel();
    }
  }, [token]);

  useEffect(() => {
    if (token && !paymentIntentFetched) {
      paymentIntentFetched = true;

      const trackedQueryStringBuilder = new TrackedQueryStringBuilder();

      trackedQueryStringBuilder
        .addQueryParamFromSessionStorage("ScCid", "_scCid")
        .addQueryParamFromSessionStorage("TwClid", "_twClid")
        .addQueryParamFromSessionStorage("ttclid", "_ttclid")
        .addQueryParamFromSessionStorage("vclid", "_vclid");

      const queryString = trackedQueryStringBuilder.build();

      console.log(queryString);

      createMyPaymentIntent(
        {
          lookupKey: product?.purchaseProviderKey,
          email,
          promoCode: promoCode || "",
          clickId: "",
          queryString,
          ignoreEphemeralKey: true,
        },
        undefined,
        (error: any) => {
          console.log(error);

          createBehaviourEvent({
            type: "PAY_ERROR",
            eventDate: new Date().toISOString(),
            userId,
            appVersion: "12.6.9",
            os: "web",
            country: "",
            deviceModel: "",
            osVersion: "",
            extraArguments: {
              error: error[0].message,
            },
          });

          setTimeout(() => {
            onCancel();
          }, 2200);
        }
      );
    }
  }, [
    token,
    userId,
    promoCode,
    product.purchaseProviderKey,
    product.subscriptionPeriod,
  ]);

  return (
    <div className="w-full">
      {createMyPaymentIntentStatus !== "loading" &&
      createMyPaymentIntentData ? (
        <div>
          <div className="mt-[14px] flex items-center justify-start md:flex-col md:justify-center">
            <TranslatedText
              text="subscription.titles.get your subscription"
              className="text-center text-[40px] font-bold text-black dark:text-white"
            />
          </div>
          <div className="mt-[37px] flex w-full items-start justify-center gap-x-[66px]">
            <div className="order-2 mt-[43px] w-full md:order-1 md:mt-0 md:w-[600px]">
              <Elements
                stripe={loadStripe(createMyPaymentIntentData.publishableKey)}
                options={{
                  clientSecret: createMyPaymentIntentData.clientSecret,
                }}
              >
                <Form
                  clientSecret={createMyPaymentIntentData.clientSecret}
                  onCancel={onCancel}
                  onSuccess={onSuccess}
                  product={product}
                  promoCode={promoCode}
                  loadingStyle={loadingStyle}
                  price={price}
                />
              </Elements>
            </div>
            <div className="order-1 w-full max-w-[394px] md:order-2 md:w-[394px]">
              <TranslatedText
                text="subscription.titles.your plan"
                className="text-right text-[24px] font-medium leading-[36px] text-black dark:text-white"
              />
              <div className="dark:border-dark-primary mt-4 w-full overflow-hidden rounded-[18px] border border-primary md:mt-3">
                <div className="mt-3 flex w-full items-center justify-between px-4 text-black dark:text-white">
                  <div className="text-right text-[18px] font-medium">
                    {mapProductName()}
                  </div>
                  <div className="flex text-[18px] font-medium tracking-[-0.8px] md:text-[20px] md:leading-[30px]">
                    <div>{product.currencyCode}</div>
                    <div className="ms-1">{product.amount?.toFixed(2)}</div>
                  </div>
                </div>
                {product.initialAmount && product.amount ? (
                  <div className="mt-3 flex w-full items-center justify-between px-4 text-black dark:text-white">
                    <TranslatedText
                      text="subscription.titles.secret discount"
                      className="text-[14px] font-medium md:text-[15px] md:leading-[22px]"
                    />
                    <div>
                      {(
                        ((product.amount - product.initialAmount) /
                          product.amount) *
                        100
                      ).toFixed(0) + "%"}
                    </div>
                  </div>
                ) : null}
                <div className="mt-5 flex items-center justify-between bg-[#38A7B424] px-4 py-4 dark:bg-[#89B8E2]">
                  <TranslatedText
                    text="subscription.titles.total price"
                    className="text-[18px] font-medium text-black md:text-[20px] md:leading-[30px] dark:text-white"
                  />
                  {product.initialAmount ? (
                    <div className="">
                      <div className="flex text-[18px] font-medium tracking-[-0.8px] text-black md:text-[20px] md:leading-[30px] dark:text-white">
                        <div>{product.currencyCode}</div>
                        <div className="ms-1">
                          {product.initialAmount?.toFixed(2)}
                        </div>
                      </div>
                      <div className="relative flex justify-end text-[15px] font-normal tracking-[-0.4px] text-black line-through decoration-[#FF0000] opacity-50 md:text-[15px] md:leading-[22px] dark:text-white">
                        <div>{product.currencyCode}</div>
                        <div className="">{product.amount?.toFixed(2)}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex text-[18px] font-medium tracking-[-0.8px] text-black md:text-[20px] md:leading-[30px] dark:text-white">
                      <div>{product.currencyCode}</div>
                      <div className="ms-1">{product.amount?.toFixed(2)}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : createMyPaymentIntentStatus === "loading" ? (
        <div className="flex flex-col items-center justify-center">
          <div className="mx-auto h-14 w-14">
            <div className="h-full w-full">
              <div
                className={`h-full w-full animate-spin rounded-full border-[3px] border-black border-b-transparent dark:border-white dark:border-b-transparent`}
              ></div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const Form = ({
  clientSecret,
  onCancel,
  onSuccess,
  product,
  promoCode,
  loadingStyle = "border-white",
  price,
}: {
  clientSecret: string;
  onCancel: () => void;
  onSuccess: () => void | Promise<void>;
  product: Api.Server.ProductGroupProductOutput;
  promoCode?: string;
  loadingStyle?: string;
  price?: string | JSX.Element;
}) => {
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(true);

  const stripe = useStripe();

  const elements = useElements();

  const options = useOptions();

  const onSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }

    const { purchaseProviderKey, currencyCode, initialAmount, amount } =
      product;

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();

    if (submitError) {
      console.log(submitError);

      return;
    } else {
      setIsSubmitLoading(true);
    }

    const result = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url:
          window.location.origin +
          `/?provider=Stripe&key=${purchaseProviderKey}&currencyCode=${
            currencyCode && encodeURIComponent(currencyCode)
          }&promoCode=${promoCode}&price=${initialAmount || amount}`,
      },
      // Uncomment below if you only want redirect for redirect-based payments
      redirect: "if_required",
    });

    // const result = await stripe.confirmCardPayment(clientSecret, {
    //   payment_method: {
    //     card: elements.getElement(CardElement) as StripeCardElement,
    //   },
    // });

    setIsSubmitLoading(false);

    if (result.error) {
      const errorMsg = getArabicStripeErrorMessage(
        result.error?.code as StripeErrorCode | undefined,
        result.error?.decline_code as StripeDeclineCode | undefined
      );
      console.log(result);
      console.log(result.error.message);
      console.log(errorMsg);
      setAlert({
        for: "modal",
        show: true,
        messages: [
          {
            code: 400,
            field: null,
            message: errorMsg,
          },
        ],
        type: AlertType.ERROR,
      });
    }

    await onSuccess();
  };

  return (
    <div
      className={`relative ${
        isFormLoading
          ? "bg-transparent"
          : "dark:border-dark-primary border border-primary bg-[#F9FAFB]"
      } rounded-[19px] p-4`}
      dir="ltr"
    >
      {isFormLoading && (
        <div className="absolute left-0 top-0 z-20 h-full w-full">
          <div className="mx-auto mt-[19px] h-14 w-14 md:mt-10">
            <div className="h-full w-full">
              <div
                className={`h-full w-full animate-spin rounded-full border-[3px] border-black border-b-transparent dark:border-white dark:border-b-transparent`}
              ></div>
            </div>
          </div>
        </div>
      )}
      <ExpressCheckoutElement
        onConfirm={(event) => {
          onSubmit();
        }}
      />
      <PaymentElement
        options={options}
        onReady={() => {
          setIsFormLoading(false);
        }}
      />
      {/* <CardElement
          options={options}
          onReady={() => {
            setIsFormLoading(false);
          }}
        /> */}
      {!isFormLoading && (
        <>
          <div className="mx-auto mt-10 w-[358px]">
            <div className="h-[50px] w-full">
              <ShadcnButton
                className="!flex h-full w-full text-white"
                onClick={onSubmit}
                isLoading={isSubmitLoading}
                disabled={isSubmitLoading}
              >
                <TranslatedText
                  text={`subscription.buttons.${"checkout"}`}
                  className="text-[20px] font-bold leading-[28px] text-white"
                />
              </ShadcnButton>
            </div>
          </div>
          {/* <div className="mt-4">
              <ButtonHeadless action={onCancel} bgStyle="bg-transparent">
                <div className="text-[#666c71]">
                  <p>رجوع</p>
                </div>
              </ButtonHeadless>
            </div> */}
        </>
      )}
    </div>
  );
};

const useOptions = () => {
  const options = useMemo<StripeCheckoutPaymentElementOptions>(
    () => ({
      layout: "accordion",
      paymentMethodOrder: ["apple_pay", "google_pay", "card"],
    }),
    []
  );

  return options;
};

export const enum StripeErrorCode {
  AccountClosed = "account_closed",
  AccountCountryInvalidAddress = "account_country_invalid_address",
  AccountErrorCountryChangeRequiresAdditionalSteps = "account_error_country_change_requires_additional_steps",
  AccountInformationMismatch = "account_information_mismatch",
  AccountInvalid = "account_invalid",
  AccountNumberInvalid = "account_number_invalid",
  AcssDebitSessionIncomplete = "acss_debit_session_incomplete",
  AlipayUpgradeRequired = "alipay_upgrade_required",
  AmountTooLarge = "amount_too_large",
  AmountTooSmall = "amount_too_small",
  ApiKeyExpired = "api_key_expired",
  ApplicationFeesNotAllowed = "application_fees_not_allowed",
  AuthenticationRequired = "authentication_required",
  BalanceInsufficient = "balance_insufficient",
  BalanceInvalidParameter = "balance_invalid_parameter",
  BankAccountBadRoutingNumbers = "bank_account_bad_routing_numbers",
  BankAccountDeclined = "bank_account_declined",
  BankAccountExists = "bank_account_exists",
  BankAccountRestricted = "bank_account_restricted",
  BankAccountUnusable = "bank_account_unusable",
  BankAccountUnverified = "bank_account_unverified",
  BankAccountVerificationFailed = "bank_account_verification_failed",
  BillingInvalidMandate = "billing_invalid_mandate",
  BillingPolicyRemoteFunctionResponseInvalid = "billing_policy_remote_function_response_invalid",
  BillingPolicyRemoteFunctionTimeout = "billing_policy_remote_function_timeout",
  BillingPolicyRemoteFunctionUnexpectedStatusCode = "billing_policy_remote_function_unexpected_status_code",
  BillingPolicyRemoteFunctionUnreachable = "billing_policy_remote_function_unreachable",
  BitcoinUpgradeRequired = "bitcoin_upgrade_required",
  CaptureChargeAuthorizationExpired = "capture_charge_authorization_expired",
  CaptureUnauthorizedPayment = "capture_unauthorized_payment",
  CardDeclineRateLimitExceeded = "card_decline_rate_limit_exceeded",
  CardDeclined = "card_declined",
  CardholderPhoneNumberRequired = "cardholder_phone_number_required",
  ChargeAlreadyCaptured = "charge_already_captured",
  ChargeAlreadyRefunded = "charge_already_refunded",
  ChargeDisputed = "charge_disputed",
  ChargeExceedsSourceLimit = "charge_exceeds_source_limit",
  ChargeExpiredForCapture = "charge_expired_for_capture",
  ChargeInvalidParameter = "charge_invalid_parameter",
  ChargeNotRefundable = "charge_not_refundable",
  ClearingCodeUnsupported = "clearing_code_unsupported",
  CountryCodeInvalid = "country_code_invalid",
  CountryUnsupported = "country_unsupported",
  CouponExpired = "coupon_expired",
  CustomerMaxPaymentMethods = "customer_max_payment_methods",
  CustomerMaxSubscriptions = "customer_max_subscriptions",
  CustomerTaxLocationInvalid = "customer_tax_location_invalid",
  DebitNotAuthorized = "debit_not_authorized",
  EmailInvalid = "email_invalid",
  ExpiredCard = "expired_card",
  FinancialConnectionsAccountInactive = "financial_connections_account_inactive",
  FinancialConnectionsNoSuccessfulTransactionRefresh = "financial_connections_no_successful_transaction_refresh",
  ForwardingApiInactive = "forwarding_api_inactive",
  ForwardingApiInvalidParameter = "forwarding_api_invalid_parameter",
  ForwardingApiUpstreamConnectionError = "forwarding_api_upstream_connection_error",
  ForwardingApiUpstreamConnectionTimeout = "forwarding_api_upstream_connection_timeout",
  IdempotencyKeyInUse = "idempotency_key_in_use",
  IncorrectAddress = "incorrect_address",
  IncorrectCvc = "incorrect_cvc",
  IncorrectNumber = "incorrect_number",
  IncorrectZip = "incorrect_zip",
  InstantPayoutsConfigDisabled = "instant_payouts_config_disabled",
  InstantPayoutsCurrencyDisabled = "instant_payouts_currency_disabled",
  InstantPayoutsLimitExceeded = "instant_payouts_limit_exceeded",
  InstantPayoutsUnsupported = "instant_payouts_unsupported",
  InsufficientFunds = "insufficient_funds",
  IntentInvalidState = "intent_invalid_state",
  IntentVerificationMethodMissing = "intent_verification_method_missing",
  InvalidCardType = "invalid_card_type",
  InvalidCharacters = "invalid_characters",
  InvalidChargeAmount = "invalid_charge_amount",
  InvalidCvc = "invalid_cvc",
  InvalidExpiryMonth = "invalid_expiry_month",
  InvalidExpiryYear = "invalid_expiry_year",
  InvalidNumber = "invalid_number",
  InvalidSourceUsage = "invalid_source_usage",
  InvalidTaxLocation = "invalid_tax_location",
  InvoiceNoCustomerLineItems = "invoice_no_customer_line_items",
  InvoiceNoPaymentMethodTypes = "invoice_no_payment_method_types",
  InvoiceNoSubscriptionLineItems = "invoice_no_subscription_line_items",
  InvoiceNotEditable = "invoice_not_editable",
  InvoiceOnBehalfOfNotEditable = "invoice_on_behalf_of_not_editable",
  InvoicePaymentIntentRequiresAction = "invoice_payment_intent_requires_action",
  InvoiceUpcomingNone = "invoice_upcoming_none",
  LivemodeMismatch = "livemode_mismatch",
  LockTimeout = "lock_timeout",
  Missing = "missing",
  NoAccount = "no_account",
  NotAllowedOnStandardAccount = "not_allowed_on_standard_account",
  OutOfInventory = "out_of_inventory",
  OwnershipDeclarationNotAllowed = "ownership_declaration_not_allowed",
  ParameterInvalidEmpty = "parameter_invalid_empty",
  ParameterInvalidInteger = "parameter_invalid_integer",
  ParameterInvalidStringBlank = "parameter_invalid_string_blank",
  ParameterInvalidStringEmpty = "parameter_invalid_string_empty",
  ParameterMissing = "parameter_missing",
  ParameterUnknown = "parameter_unknown",
  ParametersExclusive = "parameters_exclusive",
  PaymentIntentActionRequired = "payment_intent_action_required",
  PaymentIntentAuthenticationFailure = "payment_intent_authentication_failure",
  PaymentIntentFxQuoteInvalid = "payment_intent_fx_quote_invalid",
  PaymentIntentIncompatiblePaymentMethod = "payment_intent_incompatible_payment_method",
  PaymentIntentInvalidParameter = "payment_intent_invalid_parameter",
  PaymentIntentKonbiniRejectedConfirmationNumber = "payment_intent_konbini_rejected_confirmation_number",
  PaymentIntentMandateInvalid = "payment_intent_mandate_invalid",
  PaymentIntentPaymentAttemptExpired = "payment_intent_payment_attempt_expired",
  PaymentIntentPaymentAttemptFailed = "payment_intent_payment_attempt_failed",
  PaymentIntentUnexpectedState = "payment_intent_unexpected_state",
  PaymentMethodBankAccountAlreadyVerified = "payment_method_bank_account_already_verified",
  PaymentMethodBankAccountBlocked = "payment_method_bank_account_blocked",
  PaymentMethodBillingDetailsAddressMissing = "payment_method_billing_details_address_missing",
  PaymentMethodConfigurationFailures = "payment_method_configuration_failures",
  PaymentMethodCurrencyMismatch = "payment_method_currency_mismatch",
  PaymentMethodCustomerDecline = "payment_method_customer_decline",
  PaymentMethodInvalidParameter = "payment_method_invalid_parameter",
  PaymentMethodInvalidParameterTestmode = "payment_method_invalid_parameter_testmode",
  PaymentMethodMicrodepositFailed = "payment_method_microdeposit_failed",
  PaymentMethodMicrodepositVerificationAmountsInvalid = "payment_method_microdeposit_verification_amounts_invalid",
  PaymentMethodMicrodepositVerificationAmountsMismatch = "payment_method_microdeposit_verification_amounts_mismatch",
  PaymentMethodMicrodepositVerificationAttemptsExceeded = "payment_method_microdeposit_verification_attempts_exceeded",
  PaymentMethodMicrodepositVerificationDescriptorCodeMismatch = "payment_method_microdeposit_verification_descriptor_code_mismatch",
  PaymentMethodMicrodepositVerificationTimeout = "payment_method_microdeposit_verification_timeout",
  PaymentMethodNotAvailable = "payment_method_not_available",
  PaymentMethodProviderDecline = "payment_method_provider_decline",
  PaymentMethodProviderTimeout = "payment_method_provider_timeout",
  PaymentMethodUnactivated = "payment_method_unactivated",
  PaymentMethodUnexpectedState = "payment_method_unexpected_state",
  PaymentMethodUnsupportedType = "payment_method_unsupported_type",
  PayoutReconciliationNotReady = "payout_reconciliation_not_ready",
  PayoutsLimitExceeded = "payouts_limit_exceeded",
  PayoutsNotAllowed = "payouts_not_allowed",
  PlatformAccountRequired = "platform_account_required",
  PlatformApiKeyExpired = "platform_api_key_expired",
  PostalCodeInvalid = "postal_code_invalid",
  ProcessingError = "processing_error",
  ProductInactive = "product_inactive",
  ProgressiveOnboardingLimitExceeded = "progressive_onboarding_limit_exceeded",
  RateLimit = "rate_limit",
  ReferToCustomer = "refer_to_customer",
  RefundDisputedPayment = "refund_disputed_payment",
  ResourceAlreadyExists = "resource_already_exists",
  ResourceMissing = "resource_missing",
  ReturnIntentAlreadyProcessed = "return_intent_already_processed",
  RoutingNumberInvalid = "routing_number_invalid",
  SecretKeyRequired = "secret_key_required",
  SepaUnsupportedAccount = "sepa_unsupported_account",
  SetupAttemptFailed = "setup_attempt_failed",
  SetupIntentAuthenticationFailure = "setup_intent_authentication_failure",
  SetupIntentInvalidParameter = "setup_intent_invalid_parameter",
  SetupIntentMandateInvalid = "setup_intent_mandate_invalid",
  SetupIntentSetupAttemptExpired = "setup_intent_setup_attempt_expired",
  SetupIntentUnexpectedState = "setup_intent_unexpected_state",
  ShippingAddressInvalid = "shipping_address_invalid",
  ShippingCalculationFailed = "shipping_calculation_failed",
  SkuInactive = "sku_inactive",
  StateUnsupported = "state_unsupported",
  StatusTransitionInvalid = "status_transition_invalid",
  StripeTaxInactive = "stripe_tax_inactive",
  TaxIdInvalid = "tax_id_invalid",
  TaxesCalculationFailed = "taxes_calculation_failed",
  TerminalLocationCountryUnsupported = "terminal_location_country_unsupported",
  TerminalReaderBusy = "terminal_reader_busy",
  TerminalReaderHardwareFault = "terminal_reader_hardware_fault",
  TerminalReaderOffline = "terminal_reader_offline",
  TerminalReaderTimeout = "terminal_reader_timeout",
  TestmodeChargesOnly = "testmode_charges_only",
  TlsVersionUnsupported = "tls_version_unsupported",
  TokenAlreadyUsed = "token_already_used",
  TokenInUse = "token_in_use",
  TransferSourceBalanceParametersMismatch = "transfer_source_balance_parameters_mismatch",
  TransfersNotAllowed = "transfers_not_allowed",
  UrlInvalid = "url_invalid",
  WechatPayUpgradeRequired = "wechat_pay_upgrade_required",
  ZipCodeAndAddressMismatch = "zip_code_and_address_mismatch",
  ZipCodeAndCardAddressMismatch = "zip_code_and_card_address_mismatch",
  ZipCodeInvalid = "zip_code_invalid",
}

export class TrackedQueryStringBuilder {
  private queryParams: string[] = [];

  constructor() {
    this.queryParams = [];
  }

  addQueryParamFromSessionStorage(parameterName: string, storageKey: string) {
    const value = window.sessionStorage.getItem(storageKey);
    if (value) {
      this.queryParams = [...this.queryParams, `${parameterName}=${value}`];
    }

    return this;
  }

  addQueryParam(parameterName: string, value: string) {
    this.queryParams = [...this.queryParams, `${parameterName}=${value}`];
    return this;
  }

  build() {
    return `?${this.queryParams.join("&")}`;
  }
}

export function getArabicErrorMessage(
  errorCode?: StripeErrorCode | null
): string {
  switch (errorCode) {
    case StripeErrorCode.AccountClosed:
      return "الحساب مغلق";
    case StripeErrorCode.AccountCountryInvalidAddress:
      return "عنوان الحساب في الدولة غير صالح";
    case StripeErrorCode.AccountErrorCountryChangeRequiresAdditionalSteps:
      return "تغيير الدولة يتطلب خطوات إضافية";
    case StripeErrorCode.AccountInformationMismatch:
      return "تعارض في معلومات الحساب";
    case StripeErrorCode.AccountInvalid:
      return "الحساب غير صالح";
    case StripeErrorCode.AccountNumberInvalid:
      return "رقم الحساب غير صالح";
    case StripeErrorCode.AcssDebitSessionIncomplete:
      return "جلسة الخصم ACSS غير مكتملة";
    case StripeErrorCode.AlipayUpgradeRequired:
      return "ترقية Alipay مطلوبة";
    case StripeErrorCode.AmountTooLarge:
      return "المبلغ كبير جدًا";
    case StripeErrorCode.AmountTooSmall:
      return "المبلغ صغير جدًا";
    case StripeErrorCode.ApiKeyExpired:
      return "مفتاح API منتهي الصلاحية";
    case StripeErrorCode.ApplicationFeesNotAllowed:
      return "الرسوم التطبيقية غير مسموح بها";
    case StripeErrorCode.AuthenticationRequired:
      return "المصادقة مطلوبة";
    case StripeErrorCode.BalanceInsufficient:
      return "الرصيد غير كافٍ";
    case StripeErrorCode.BalanceInvalidParameter:
      return "معلمة الرصيد غير صالحة";
    case StripeErrorCode.BankAccountBadRoutingNumbers:
      return "أرقام التوجيه المصرفي غير صحيحة";
    case StripeErrorCode.BankAccountDeclined:
      return "تم رفض الحساب المصرفي";
    case StripeErrorCode.BankAccountExists:
      return "الحساب المصرفي موجود بالفعل";
    case StripeErrorCode.BankAccountRestricted:
      return "الحساب المصرفي مقيد";
    case StripeErrorCode.BankAccountUnusable:
      return "الحساب المصرفي غير قابل للاستخدام";
    case StripeErrorCode.BankAccountUnverified:
      return "الحساب المصرفي غير مؤكد";
    case StripeErrorCode.BankAccountVerificationFailed:
      return "فشل في تأكيد الحساب المصرفي";
    case StripeErrorCode.BillingInvalidMandate:
      return "تفويض الفواتير غير صالح";
    case StripeErrorCode.BillingPolicyRemoteFunctionResponseInvalid:
      return "استجابة وظيفة سياسة الفواتير البعيدة غير صالحة";
    case StripeErrorCode.BillingPolicyRemoteFunctionTimeout:
      return "انتهت مهلة وظيفة سياسة الفواتير البعيدة";
    case StripeErrorCode.BillingPolicyRemoteFunctionUnexpectedStatusCode:
      return "رمز الحالة غير المتوقع لوظيفة سياسة الفواتير البعيدة";
    case StripeErrorCode.BillingPolicyRemoteFunctionUnreachable:
      return "وظيفة سياسة الفواتير البعيدة غير قابلة للوصول";
    case StripeErrorCode.BitcoinUpgradeRequired:
      return "ترقية Bitcoin مطلوبة";
    case StripeErrorCode.CaptureChargeAuthorizationExpired:
      return "انتهت صلاحية تفويض التقاط الشحنة";
    case StripeErrorCode.CaptureUnauthorizedPayment:
      return "دفع غير مصرح به";
    case StripeErrorCode.CardDeclineRateLimitExceeded:
      return "تم تجاوز حد معدل رفض البطاقة";
    case StripeErrorCode.CardDeclined:
      return "تم رفض البطاقة";
    case StripeErrorCode.CardholderPhoneNumberRequired:
      return "رقم هاتف حامل البطاقة مطلوب";
    case StripeErrorCode.ChargeAlreadyCaptured:
      return "تم التقاط الشحنة بالفعل";
    case StripeErrorCode.ChargeAlreadyRefunded:
      return "تم رد الشحنة بالفعل";
    case StripeErrorCode.ChargeDisputed:
      return "الشحنة متنازع عليها";
    case StripeErrorCode.ChargeExceedsSourceLimit:
      return "الشحنة تتجاوز حد المصدر";
    case StripeErrorCode.ChargeExpiredForCapture:
      return "انتهت صلاحية الشحنة للتقاطها";
    case StripeErrorCode.ChargeInvalidParameter:
      return "معلمة الشحنة غير صالحة";
    case StripeErrorCode.ChargeNotRefundable:
      return "الشحنة غير قابلة للاسترداد";
    case StripeErrorCode.ClearingCodeUnsupported:
      return "رمز المقاصة غير مدعوم";
    case StripeErrorCode.CountryCodeInvalid:
      return "رمز الدولة غير صالح";
    case StripeErrorCode.CountryUnsupported:
      return "الدولة غير مدعومة";
    case StripeErrorCode.CouponExpired:
      return "الكوبون منتهي الصلاحية";
    case StripeErrorCode.CustomerMaxPaymentMethods:
      return "تم تجاوز الحد الأقصى لطرق الدفع للعميل";
    case StripeErrorCode.CustomerMaxSubscriptions:
      return "تم تجاوز الحد الأقصى للاشتراكات للعميل";
    case StripeErrorCode.CustomerTaxLocationInvalid:
      return "موقع الضريبة للعميل غير صالح";
    case StripeErrorCode.DebitNotAuthorized:
      return "الخصم غير مصرح به";
    case StripeErrorCode.EmailInvalid:
      return "البريد الإلكتروني غير صالح";
    case StripeErrorCode.ExpiredCard:
      return "البطاقة منتهية الصلاحية";
    case StripeErrorCode.FinancialConnectionsAccountInactive:
      return "الحساب غير نشط";
    case StripeErrorCode.FinancialConnectionsNoSuccessfulTransactionRefresh:
      return "لم يتم تحديث المعاملة بنجاح";
    case StripeErrorCode.ForwardingApiInactive:
      return "واجهة برمجة التطبيقات غير نشطة";
    case StripeErrorCode.ForwardingApiInvalidParameter:
      return "معلمة واجهة برمجة التطبيقات غير صالحة";
    case StripeErrorCode.ForwardingApiUpstreamConnectionError:
      return "خطأ في اتصال واجهة برمجة التطبيقات";
    case StripeErrorCode.ForwardingApiUpstreamConnectionTimeout:
      return "انتهاء مهلة اتصال واجهة برمجة التطبيقات";
    case StripeErrorCode.IdempotencyKeyInUse:
      return "مفتاح التماثل قيد الاستخدام";
    case StripeErrorCode.IncorrectAddress:
      return "العنوان غير صحيح";
    case StripeErrorCode.IncorrectCvc:
      return "رمز التحقق من البطاقة غير صحيح";
    case StripeErrorCode.IncorrectNumber:
      return "الرقم غير صحيح";
    case StripeErrorCode.IncorrectZip:
      return "الرمز البريدي غير صحيح";
    case StripeErrorCode.InstantPayoutsConfigDisabled:
      return "التكوين الفوري للدفع معطل";
    case StripeErrorCode.InstantPayoutsCurrencyDisabled:
      return "عملة الدفع الفوري معطلة";
    case StripeErrorCode.InstantPayoutsLimitExceeded:
      return "تم تجاوز حد الدفع الفوري";
    case StripeErrorCode.InstantPayoutsUnsupported:
      return "الدفع الفوري غير مدعوم";
    case StripeErrorCode.InsufficientFunds:
      return "الأموال غير كافية";
    case StripeErrorCode.IntentInvalidState:
      return "حالة النية غير صالحة";
    case StripeErrorCode.IntentVerificationMethodMissing:
      return "طريقة التحقق من النية مفقودة";
    case StripeErrorCode.InvalidCardType:
      return "نوع البطاقة غير صالح";
    case StripeErrorCode.InvalidCharacters:
      return "أحرف غير صالحة";
    case StripeErrorCode.InvalidChargeAmount:
      return "مبلغ الشحنة غير صالح";
    case StripeErrorCode.InvalidCvc:
      return "رمز التحقق من البطاقة غير صالح";
    case StripeErrorCode.InvalidExpiryMonth:
      return "شهر انتهاء الصلاحية غير صالح";
    case StripeErrorCode.InvalidExpiryYear:
      return "سنة انتهاء الصلاحية غير صالحة";
    case StripeErrorCode.InvalidNumber:
      return "الرقم غير صالح";
    case StripeErrorCode.InvalidSourceUsage:
      return "استخدام المصدر غير صالح";
    case StripeErrorCode.InvalidTaxLocation:
      return "موقع الضريبة غير صالح";
    case StripeErrorCode.InvoiceNoCustomerLineItems:
      return "لا توجد عناصر خطية للعميل في الفاتورة";
    case StripeErrorCode.InvoiceNoPaymentMethodTypes:
      return "لا توجد طرق دفع في الفاتورة";
    case StripeErrorCode.InvoiceNoSubscriptionLineItems:
      return "لا توجد عناصر اشتراك في الفاتورة";
    case StripeErrorCode.InvoiceNotEditable:
      return "الفاتورة غير قابلة للتحرير";
    case StripeErrorCode.InvoiceOnBehalfOfNotEditable:
      return "الفاتورة نيابة عن غير قابلة للتحرير";
    case StripeErrorCode.InvoicePaymentIntentRequiresAction:
      return "نية دفع الفاتورة تتطلب إجراءً";
    case StripeErrorCode.InvoiceUpcomingNone:
      return "لا توجد فواتير قادمة";
    case StripeErrorCode.LivemodeMismatch:
      return "عدم تطابق وضع الحياة";
    case StripeErrorCode.LockTimeout:
      return "انتهاء مهلة القفل";
    case StripeErrorCode.Missing:
      return "مفقود";
    case StripeErrorCode.NoAccount:
      return "لا يوجد حساب";
    case StripeErrorCode.NotAllowedOnStandardAccount:
      return "غير مسموح به على الحساب القياسي";
    case StripeErrorCode.OutOfInventory:
      return "نفاد المخزون";
    case StripeErrorCode.OwnershipDeclarationNotAllowed:
      return "إعلان الملكية غير مسموح به";
    case StripeErrorCode.ParameterInvalidEmpty:
      return "معلمة فارغة غير صالحة";
    case StripeErrorCode.ParameterInvalidInteger:
      return "معلمة عدد صحيح غير صالحة";
    case StripeErrorCode.ParameterInvalidStringBlank:
      return "معلمة سلسلة غير صالحة";
    case StripeErrorCode.ParameterInvalidStringEmpty:
      return "معلمة سلسلة فارغة غير صالحة";
    case StripeErrorCode.ParameterMissing:
      return "معلمة مفقودة";
    case StripeErrorCode.ParameterUnknown:
      return "معلمة غير معروفة";
    case StripeErrorCode.ParametersExclusive:
      return "معلمات حصرية";
    case StripeErrorCode.PaymentIntentActionRequired:
      return "نية الدفع تتطلب إجراءً";
    case StripeErrorCode.PaymentIntentAuthenticationFailure:
      return "فشل في مصادقة نية الدفع";
    case StripeErrorCode.PaymentIntentFxQuoteInvalid:
      return "اقتباس نية الدفع غير صالح";
    case StripeErrorCode.PaymentIntentIncompatiblePaymentMethod:
      return "طريقة دفع غير متوافقة";
    case StripeErrorCode.PaymentIntentInvalidParameter:
      return "معلمة نية الدفع غير صالحة";
    case StripeErrorCode.PaymentIntentKonbiniRejectedConfirmationNumber:
      return "تم رفض رقم تأكيد نية الدفع Konbini";
    case StripeErrorCode.PaymentIntentMandateInvalid:
      return "تفويض نية الدفع غير صالح";
    case StripeErrorCode.PaymentIntentPaymentAttemptExpired:
      return "انتهاء مهلة محاولة دفع نية الدفع";
    case StripeErrorCode.PaymentIntentPaymentAttemptFailed:
      return "فشل محاولة دفع نية الدفع";
    case StripeErrorCode.PaymentIntentUnexpectedState:
      return "حالة نية الدفع غير متوقعة";
    case StripeErrorCode.PaymentMethodBankAccountAlreadyVerified:
      return "الحساب المصرفي لطريقة الدفع مؤكد بالفعل";
    case StripeErrorCode.PaymentMethodBankAccountBlocked:
      return "الحساب المصرفي لطريقة الدفع محظور";
    case StripeErrorCode.PaymentMethodBillingDetailsAddressMissing:
      return "تفاصيل الفواتير الخاصة بطريقة الدفع مفقودة";
    case StripeErrorCode.PaymentMethodConfigurationFailures:
      return "فشل في تكوين طريقة الدفع";
    case StripeErrorCode.PaymentMethodCurrencyMismatch:
      return "عدم تطابق عملة طريقة الدفع";
    case StripeErrorCode.PaymentMethodCustomerDecline:
      return "رفض العميل لطريقة الدفع";
    case StripeErrorCode.PaymentMethodInvalidParameter:
      return "معلمة طريقة الدفع غير صالحة";
    case StripeErrorCode.PaymentMethodInvalidParameterTestmode:
      return "معلمة طريقة الدفع غير صالحة في وضع الاختبار";
    case StripeErrorCode.PaymentMethodMicrodepositFailed:
      return "فشل الإيداع الجزئي لطريقة الدفع";
    case StripeErrorCode.PaymentMethodMicrodepositVerificationAmountsInvalid:
      return "مبالغ تأكيد الإيداع الجزئي لطريقة الدفع غير صالحة";
    case StripeErrorCode.PaymentMethodMicrodepositVerificationAmountsMismatch:
      return "عدم تطابق مبالغ تأكيد الإيداع الجزئي لطريقة الدفع";
    case StripeErrorCode.PaymentMethodMicrodepositVerificationAttemptsExceeded:
      return "تم تجاوز محاولات تأكيد الإيداع الجزئي لطريقة الدفع";
    case StripeErrorCode.PaymentMethodMicrodepositVerificationDescriptorCodeMismatch:
      return "عدم تطابق رمز وصف تأكيد الإيداع الجزئي لطريقة الدفع";
    case StripeErrorCode.PaymentMethodMicrodepositVerificationTimeout:
      return "انتهاء مهلة تأكيد الإيداع الجزئي لطريقة الدفع";
    case StripeErrorCode.PaymentMethodNotAvailable:
      return "طريقة الدفع غير متوفرة";
    case StripeErrorCode.PaymentMethodProviderDecline:
      return "رفض مزود طريقة الدفع";
    case StripeErrorCode.PaymentMethodProviderTimeout:
      return "انتهاء مهلة مزود طريقة الدفع";
    case StripeErrorCode.PaymentMethodUnactivated:
      return "طريقة الدفع غير مفعلة";
    case StripeErrorCode.PaymentMethodUnexpectedState:
      return "حالة غير متوقعة لطريقة الدفع";
    case StripeErrorCode.PaymentMethodUnsupportedType:
      return "نوع غير مدعوم لطريقة الدفع";
    case StripeErrorCode.PayoutReconciliationNotReady:
      return "تسوية الدفع غير جاهزة";
    case StripeErrorCode.PayoutsLimitExceeded:
      return "تم تجاوز حد الدفع";
    case StripeErrorCode.PayoutsNotAllowed:
      return "الدفع غير مسموح به";
    case StripeErrorCode.PlatformAccountRequired:
      return "مطلوب حساب منصة";
    case StripeErrorCode.PlatformApiKeyExpired:
      return "مفتاح واجهة برمجة التطبيقات للمنصة منتهي الصلاحية";
    case StripeErrorCode.PostalCodeInvalid:
      return "الرمز البريدي غير صالح";
    case StripeErrorCode.ProcessingError:
      return "خطأ في المعالجة";
    case StripeErrorCode.ProductInactive:
      return "المنتج غير نشط";
    case StripeErrorCode.ProgressiveOnboardingLimitExceeded:
      return "تم تجاوز حد الإعداد التقدمي";
    case StripeErrorCode.RateLimit:
      return "حد معدل تجاوز";
    case StripeErrorCode.ReferToCustomer:
      return "الإحالة إلى العميل";
    case StripeErrorCode.RefundDisputedPayment:
      return "استرداد دفع متنازع عليه";
    case StripeErrorCode.ResourceAlreadyExists:
      return "المورد موجود بالفعل";
    case StripeErrorCode.ResourceMissing:
      return "المورد مفقود";
    case StripeErrorCode.ReturnIntentAlreadyProcessed:
      return "تمت معالجة نية الإرجاع بالفعل";
    case StripeErrorCode.RoutingNumberInvalid:
      return "رقم التوجيه غير صالح";
    case StripeErrorCode.SecretKeyRequired:
      return "مفتاح سري مطلوب";
    case StripeErrorCode.SepaUnsupportedAccount:
      return "حساب غير مدعوم SEPA";
    case StripeErrorCode.SetupAttemptFailed:
      return "فشل محاولة الإعداد";
    case StripeErrorCode.SetupIntentAuthenticationFailure:
      return "فشل مصادقة نية الإعداد";
    case StripeErrorCode.SetupIntentInvalidParameter:
      return "معلمة نية الإعداد غير صالحة";
    case StripeErrorCode.SetupIntentMandateInvalid:
      return "تفويض نية الإعداد غير صالح";
    case StripeErrorCode.SetupIntentSetupAttemptExpired:
      return "انتهاء صلاحية محاولة إعداد النية";
    case StripeErrorCode.SetupIntentUnexpectedState:
      return "حالة نية الإعداد غير متوقعة";
    case StripeErrorCode.ShippingAddressInvalid:
      return "عنوان الشحن غير صالح";
    case StripeErrorCode.ShippingCalculationFailed:
      return "فشل في حساب الشحن";
    case StripeErrorCode.SkuInactive:
      return "SKU غير نشط";
    case StripeErrorCode.StateUnsupported:
      return "الولاية غير مدعومة";
    case StripeErrorCode.StatusTransitionInvalid:
      return "تحول الحالة غير صالح";
    case StripeErrorCode.StripeTaxInactive:
      return "ضريبة Stripe غير نشطة";
    case StripeErrorCode.TaxIdInvalid:
      return "معرف الضريبة غير صالح";
    case StripeErrorCode.TaxesCalculationFailed:
      return "فشل في حساب الضرائب";
    case StripeErrorCode.TerminalLocationCountryUnsupported:
      return "الدولة غير مدعومة لموقع المحطة";
    case StripeErrorCode.TerminalReaderBusy:
      return "قارئ المحطة مشغول";
    case StripeErrorCode.TerminalReaderHardwareFault:
      return "خلل في أجهزة قارئ المحطة";
    case StripeErrorCode.TerminalReaderOffline:
      return "قارئ المحطة غير متصل";
    case StripeErrorCode.TerminalReaderTimeout:
      return "انتهاء مهلة قارئ المحطة";
    case StripeErrorCode.TestmodeChargesOnly:
      return "الدفع في وضع الاختبار فقط";
    case StripeErrorCode.TlsVersionUnsupported:
      return "إصدار TLS غير مدعوم";
    case StripeErrorCode.TokenAlreadyUsed:
      return "الرمز المستخدم بالفعل";
    case StripeErrorCode.TokenInUse:
      return "الرمز قيد الاستخدام";
    case StripeErrorCode.TransferSourceBalanceParametersMismatch:
      return "عدم تطابق معلمات مصدر الرصيد";
    case StripeErrorCode.TransfersNotAllowed:
      return "التحويلات غير مسموح بها";
    case StripeErrorCode.UrlInvalid:
      return "الرابط غير صالح";
    case StripeErrorCode.WechatPayUpgradeRequired:
      return "ترقية WeChat Pay مطلوبة";
    case StripeErrorCode.ZipCodeAndAddressMismatch:
      return "عدم تطابق الرمز البريدي والعنوان";
    case StripeErrorCode.ZipCodeAndCardAddressMismatch:
      return "عدم تطابق الرمز البريدي وعنوان البطاقة";
    case StripeErrorCode.ZipCodeInvalid:
      return "الرمز البريدي غير صالح";
    default:
      return errorCode ?? "خطأ غير معروف";
  }
}

export const enum StripeDeclineCode {
  AuthenticationRequired = "authentication_required", // The card was declined because the transaction requires authentication such as 3D Secure.
  ApproveWithId = "approve_with_id", // The payment can’t be authorized.
  CallIssuer = "call_issuer", // The card was declined for an unknown reason.
  CardNotSupported = "card_not_supported", // The card does not support this type of purchase.
  CardVelocityExceeded = "card_velocity_exceeded", // The customer has exceeded the balance, credit limit, or transaction amount limit available on their card.
  CurrencyNotSupported = "currency_not_supported", // The card does not support the specified currency.
  DoNotHonor = "do_not_honor", // The card was declined for an unknown reason.
  DoNotTryAgain = "do_not_try_again", // The card was declined for an unknown reason.
  DuplicateTransaction = "duplicate_transaction", // A transaction with identical amount and credit card information was submitted very recently.
  ExpiredCard = "expired_card", // The card has expired.
  Fraudulent = "fraudulent", // The payment was declined because Stripe suspects that it’s fraudulent.
  GenericDecline = "generic_decline", // The card was declined for an unknown reason or Stripe Radar blocked the payment.
  IncorrectNumber = "incorrect_number", // The card number is incorrect.
  IncorrectCvc = "incorrect_cvc", // The CVC number is incorrect.
  IncorrectPin = "incorrect_pin", // The PIN entered is incorrect.
  IncorrectZip = "incorrect_zip", // The postal code is incorrect.
  InsufficientFunds = "insufficient_funds", // The card has insufficient funds to complete the purchase.
  InvalidAccount = "invalid_account", // The card, or account the card is connected to, is invalid.
  InvalidAmount = "invalid_amount", // The payment amount is invalid, or exceeds the amount that’s allowed.
  InvalidCvc = "invalid_cvc", // The CVC number is incorrect.
  InvalidExpiryMonth = "invalid_expiry_month", // The expiration month is invalid.
  InvalidExpiryYear = "invalid_expiry_year", // The expiration year is invalid.
  InvalidNumber = "invalid_number", // The card number is incorrect.
  InvalidPin = "invalid_pin", // The PIN entered is incorrect.
  IssuerNotAvailable = "issuer_not_available", // The card issuer couldn’t be reached, so the payment couldn’t be authorized.
  LostCard = "lost_card", // The payment was declined because the card is reported lost.
  MerchantBlacklist = "merchant_blacklist", // The payment was declined because it matches a value on the Stripe user’s block list.
  NewAccountInformationAvailable = "new_account_information_available", // The card, or account the card is connected to, is invalid.
  NoActionTaken = "no_action_taken", // The card was declined for an unknown reason.
  NotPermitted = "not_permitted", // The payment isn’t permitted.
  OfflinePinRequired = "offline_pin_required", // The card was declined because it requires a PIN.
  OnlineOrOfflinePinRequired = "online_or_offline_pin_required", // The card was declined as it requires a PIN.
  PickupCard = "pickup_card", // The customer can’t use this card to make this payment (it’s possible it was reported lost or stolen).
  PinTryExceeded = "pin_try_exceeded", // The allowable number of PIN tries was exceeded.
  ProcessingError = "processing_error", // An error occurred while processing the card.
  ReenterTransaction = "reenter_transaction", // The payment couldn’t be processed by the issuer for an unknown reason.
  RestrictedCard = "restricted_card", // The customer can’t use this card to make this payment (it’s possible it was reported lost or stolen).
  RevocationOfAllAuthorizations = "revocation_of_all_authorizations", // The card was declined for an unknown reason.
  RevocationOfAuthorization = "revocation_of_authorization", // The card was declined for an unknown reason.
  SecurityViolation = "security_violation", // The card was declined for an unknown reason.
  ServiceNotAllowed = "service_not_allowed", // The card was declined for an unknown reason.
  StolenCard = "stolen_card", // The payment was declined because the card is reported stolen.
  StopPaymentOrder = "stop_payment_order", // The card was declined for an unknown reason.
  TestmodeDecline = "testmode_decline", // A Stripe test card number was used.
  TransactionNotAllowed = "transaction_not_allowed", // The card was declined for an unknown reason.
  TryAgainLater = "try_again_later", // The card was declined for an unknown reason.
  WithdrawalCountLimitExceeded = "withdrawal_count_limit_exceeded", // The customer has exceeded the balance or credit limit available on their card.
}

export function getArabicStripeErrorMessage(
  errorCode?: StripeErrorCode | null,
  declineCode?: StripeDeclineCode | null
): string {
  switch (declineCode) {
    case StripeDeclineCode.AuthenticationRequired:
      return "المصادقة مطلوبة";
    case StripeDeclineCode.ApproveWithId:
      return "لا يمكن تفويض الدفع";
    case StripeDeclineCode.CallIssuer:
      return "تم رفض البطاقة لسبب غير معروف";
    case StripeDeclineCode.CardNotSupported:
      return "البطاقة لا تدعم هذا النوع من الشراء";
    case StripeDeclineCode.CardVelocityExceeded:
      return "تجاوز العميل الحد المسموح به للرصيد أو الائتمان أو مبلغ المعاملة المتاح على بطاقته";
    case StripeDeclineCode.CurrencyNotSupported:
      return "البطاقة لا تدعم العملة المحددة";
    case StripeDeclineCode.DoNotHonor:
      return "تم رفض البطاقة لسبب غير معروف";
    case StripeDeclineCode.DoNotTryAgain:
      return "تم رفض البطاقة لسبب غير معروف";
    case StripeDeclineCode.DuplicateTransaction:
      return "تم إرسال معاملة بنفس المبلغ ومعلومات البطاقة مؤخرًا";
    case StripeDeclineCode.ExpiredCard:
      return "البطاقة منتهية الصلاحية";
    case StripeDeclineCode.Fraudulent:
      return "تم رفض الدفع لأن Stripe يشتبه في أنه احتيالي";
    case StripeDeclineCode.GenericDecline:
      return "تم رفض البطاقة لسبب غير معروف أو قام Stripe Radar بحظر الدفع";
    case StripeDeclineCode.IncorrectNumber:
      return "رقم البطاقة غير صحيح";
    case StripeDeclineCode.IncorrectCvc:
      return "رمز التحقق من البطاقة غير صحيح";
    case StripeDeclineCode.IncorrectPin:
      return "الرقم السري المدخل غير صحيح";
    case StripeDeclineCode.IncorrectZip:
      return "الرمز البريدي غير صحيح";
    case StripeDeclineCode.InsufficientFunds:
      return "الرصيد غير كافٍ لإتمام الشراء";
    case StripeDeclineCode.InvalidAccount:
      return "البطاقة أو الحساب المتصل بها غير صالح";
    case StripeDeclineCode.InvalidAmount:
      return "مبلغ الدفع غير صالح أو يتجاوز المبلغ المسموح به";
    case StripeDeclineCode.InvalidCvc:
      return "رمز التحقق من البطاقة غير صالح";
    case StripeDeclineCode.InvalidExpiryMonth:
      return "شهر انتهاء الصلاحية غير صالح";
    case StripeDeclineCode.InvalidExpiryYear:
      return "سنة انتهاء الصلاحية غير صالحة";
    case StripeDeclineCode.InvalidNumber:
      return "رقم البطاقة غير صحيح";
    case StripeDeclineCode.InvalidPin:
      return "الرقم السري المدخل غير صحيح";
    case StripeDeclineCode.IssuerNotAvailable:
      return "تعذر الوصول إلى جهة إصدار البطاقة، لذا لم يتم تفويض الدفع";
    case StripeDeclineCode.LostCard:
      return "تم رفض الدفع لأن البطاقة مبلّغ عنها مفقودة";
    case StripeDeclineCode.MerchantBlacklist:
      return "تم رفض الدفع لأنه يطابق قيمة في قائمة الحظر لمستخدم Stripe";
    case StripeDeclineCode.NewAccountInformationAvailable:
      return "البطاقة أو الحساب المتصل بها غير صالح";
    case StripeDeclineCode.NoActionTaken:
      return "تم رفض البطاقة لسبب غير معروف";
    case StripeDeclineCode.NotPermitted:
      return "الدفع غير مسموح به";
    case StripeDeclineCode.OfflinePinRequired:
      return "تم رفض البطاقة لأنها تتطلب رمز PIN";
    case StripeDeclineCode.OnlineOrOfflinePinRequired:
      return "تم رفض البطاقة لأنها تتطلب رمز PIN";
    case StripeDeclineCode.PickupCard:
      return "لا يمكن للعميل استخدام هذه البطاقة لإجراء هذا الدفع (من الممكن أنه تم الإبلاغ عنها مفقودة أو مسروقة)";
    case StripeDeclineCode.PinTryExceeded:
      return "تم تجاوز العدد المسموح به من محاولات إدخال الرقم السري";
    case StripeDeclineCode.ProcessingError:
      return "حدث خطأ أثناء معالجة البطاقة";
    case StripeDeclineCode.ReenterTransaction:
      return "تعذر معالجة الدفع من قبل الجهة المصدرة لسبب غير معروف";
    case StripeDeclineCode.RestrictedCard:
      return "لا يمكن للعميل استخدام هذه البطاقة لإجراء هذا الدفع (من الممكن أنه تم الإبلاغ عنها مفقودة أو مسروقة)";
    case StripeDeclineCode.RevocationOfAllAuthorizations:
      return "تم رفض البطاقة لسبب غير معروف";
    case StripeDeclineCode.RevocationOfAuthorization:
      return "تم رفض البطاقة لسبب غير معروف";
    case StripeDeclineCode.SecurityViolation:
      return "تم رفض البطاقة لسبب غير معروف";
    case StripeDeclineCode.ServiceNotAllowed:
      return "تم رفض البطاقة لسبب غير معروف";
    case StripeDeclineCode.StolenCard:
      return "تم رفض الدفع لأن البطاقة مبلّغ عنها مسروقة";
    case StripeDeclineCode.StopPaymentOrder:
      return "تم رفض البطاقة لسبب غير معروف";
    case StripeDeclineCode.TestmodeDecline:
      return "تم استخدام رقم بطاقة اختبار Stripe";
    case StripeDeclineCode.TransactionNotAllowed:
      return "تم رفض البطاقة لسبب غير معروف";
    case StripeDeclineCode.TryAgainLater:
      return "تم رفض البطاقة لسبب غير معروف";
    case StripeDeclineCode.WithdrawalCountLimitExceeded:
      return "تجاوز العميل الحد المسموح به للرصيد أو الائتمان المتاح على بطاقته";
    default:
      return getArabicErrorMessage(errorCode);
  }
}
